import { FormattedMessage } from 'react-intl';

import { useLocale } from '../../context/LocaleContext';
import { EquivalentI } from '../../declarations/interfaces/calculator';
import { OffsetsT } from '../../pages/CarbonOffsetsPage/hooks/useCarbonOffsets';
import { QuestionIcon } from '../Icon/Icon';
import Tooltip from '../Tooltip/Tooltip';
import {
  Divider,
  EquivalentLabelStyled,
  EquivalentListStyled,
  FootprintSummaryWrapperStyled,
  ImageWrapperStyled,
  NumberStyled,
  OffsetLabelStyled,
  OffsetListStyled,
  TitleStyled,
} from './CarbonFootprintSummary.styled';

type CarbonFootprintSummaryPropsT = {
  equivalents: EquivalentI[];
  offsets: OffsetsT;
};

const CarbonFootprintSummary: React.FC<CarbonFootprintSummaryPropsT> = ({
  equivalents,
  offsets,
}) => {
  const { selectedLocale } = useLocale();
  const { stayOffset, landOffset, boatOffset, planeOffset, total, totalOffsetInTonnes } = offsets;

  return (
    <FootprintSummaryWrapperStyled>
      <TitleStyled level={2} isUppercase={false}>
        <FormattedMessage
          id="footprint.summary.label"
          defaultMessage="Your total carbon footprint"
        />
      </TitleStyled>

      <Divider />

      <OffsetListStyled>
        {!!stayOffset && (
          <li>
            <OffsetLabelStyled>
              <img src="/images/icons/hotel.svg" alt="hotel" />
              <FormattedMessage id="breadcrumbs.yourStay" defaultMessage="Your Stay" />
            </OffsetLabelStyled>
            <span> {stayOffset}t</span>
          </li>
        )}

        {!!planeOffset && (
          <li>
            <OffsetLabelStyled>
              <img src="/images/icons/plane.svg" alt="plane" />
              <FormattedMessage id="footprint.airTravel" defaultMessage="Air travel" />
            </OffsetLabelStyled>
            <span>{planeOffset}t</span>
          </li>
        )}

        {!!landOffset && (
          <li>
            <OffsetLabelStyled>
              <img src="/images/icons/car.svg" alt="car" />
              <FormattedMessage id="footprint.landTravel" defaultMessage="Land travel" />
            </OffsetLabelStyled>
            <span>{landOffset}t</span>
          </li>
        )}

        {!!boatOffset && (
          <li>
            <OffsetLabelStyled>
              <img src="/images/icons/boat.svg" alt="boat" />
              <FormattedMessage id="footprint.waterTravel" defaultMessage="Water travel" />
            </OffsetLabelStyled>
            <span>{boatOffset}t</span>
          </li>
        )}

        <li>
          <OffsetLabelStyled>
            <FormattedMessage id="footprint.totalCO2" defaultMessage="Total CO2e" />
          </OffsetLabelStyled>
          <span>{total}t</span>
        </li>

        <li>
          <OffsetLabelStyled>
            <FormattedMessage id="footprint.totalOffset" defaultMessage="Total to offset" />
            <Tooltip element={<QuestionIcon />}>
              <FormattedMessage
                id="footprint.totalOffset.tooltip.text"
                defaultMessage="The certificates of emission reduction are sold only in entire units, for this reason your total has ben automatically rounded up/down to the nearest whole number."
              />
            </Tooltip>
          </OffsetLabelStyled>
          <span>
            <FormattedMessage
              id="common.tonne"
              defaultMessage="{count, plural, =0 {0 tonnes} one {# tonne} other {# tonnes}}"
              values={{
                count: totalOffsetInTonnes,
              }}
            />
          </span>
        </li>
      </OffsetListStyled>

      <TitleStyled level={4}>
        <FormattedMessage
          id="footprint.co2Generated"
          defaultMessage="CO2e GENERATED IS THE SAME AS"
        />
      </TitleStyled>

      <Divider />

      <EquivalentListStyled>
        {!!equivalents.length &&
          equivalents.map(({ asset, targetActivity, targetProduct, equivalentValue }, index) => (
            <li key={index}>
              <ImageWrapperStyled>
                <img src={`/images/icons/${asset}.svg`} alt="equivalent" />
              </ImageWrapperStyled>
              <EquivalentLabelStyled>{targetActivity[selectedLocale]}</EquivalentLabelStyled>
              <NumberStyled>{equivalentValue.toLocaleString('en-US')}</NumberStyled>
              <EquivalentLabelStyled>{targetProduct[selectedLocale]}</EquivalentLabelStyled>
            </li>
          ))}
      </EquivalentListStyled>
    </FootprintSummaryWrapperStyled>
  );
};

export default CarbonFootprintSummary;
